import React, { memo, useEffect, useRef } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  arrayOf, func, number, object, oneOf, shape, string,
} from 'prop-types'

import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { VERSION_KIND } from 'Pods/ThreeDStudio/Shared/Utils/imageRun'

// Dropdown of previously generated versions, newest first. Selecting one loads it
// back onto the canvas. Closes on outside click or Escape. triggerRef (the History
// area) is treated as inside, so clicking the open trigger closes via its own
// onClick rather than reopening after a pointerdown-driven onClose.
function HistoryMenu({
  versions, currentId = null, triggerRef = null, onSelect, onClose,
}) {
  const t = useTranslation()
  const menuRef = useRef(null)

  useEffect(() => {
    const handlePointerDown = (event) => {
      const inside = menuRef.current?.contains(event.target)
        || triggerRef?.current?.contains(event.target)
      if (!inside) {
        onClose()
      }
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose, triggerRef])

  return (
    <div className="image-assistant-edit-tab__history-menu" role="menu" ref={menuRef}>
      {versions.map(version => (
        <button
          key={version.id}
          type="button"
          role="menuitem"
          className={classnames(
            'image-assistant-edit-tab__history-item',
            version.id === currentId && 'image-assistant-edit-tab__history-item--active',
          )}
          onClick={() => onSelect(version)}
        >
          <span className="image-assistant-edit-tab__history-thumb">
            <img className="image-assistant-edit-tab__history-thumb-image" src={version.url} alt="" />
          </span>
          <span className="image-assistant-edit-tab__history-text">
            <span className="image-assistant-edit-tab__history-label cgt-body-base-semibold">{version.label}</span>
            {version.kind && (
              <span className="image-assistant-edit-tab__history-kind cgt-caption">
                {t(`imageAssistantPanel.editTab.toolbar.versionKind.${version.kind}`)}
              </span>
            )}
          </span>
        </button>
      ))}
    </div>
  )
}

HistoryMenu.propTypes = {
  versions: arrayOf(shape({
    id: number.isRequired,
    url: string.isRequired,
    label: string.isRequired,
    kind: oneOf(Object.values(VERSION_KIND)),
    prompt: string,
  })).isRequired,
  currentId: number,
  triggerRef: shape({ current: object }),
  onSelect: func.isRequired,
  onClose: func.isRequired,
}

export default memo(HistoryMenu)
